import classNames from 'classnames';
import React, { useState, useRef, useEffect } from 'react';
import { FiChevronDown, FiTrash2 } from 'react-icons/fi';
import { toast } from 'react-toastify';
import { AnimatePresence, motion } from 'framer-motion';

import { useAuth } from '../../contexts/AuthContext';
import { formatDate } from '../../utils/dateUtils';
import Button from '../Button';
import { IProfile } from '../../types/Profile';
import ConfirmationModal from '../ConfirmationModal';
import StarIcon from '../../assets/icons/star-icon.svg';

import styles from './ProfileActionPanel.module.scss';

interface ProfileDropdownButtonProps {
  profiles: IProfile[];
  onSelect: (profile: IProfile) => void;
  label: string;
}

export function ProfileActionPanel({ profiles, onSelect, label }: ProfileDropdownButtonProps) {
  const { deleteProfile, activeProfile, isAuthenticated } = useAuth();
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const panelRef = useRef<HTMLDivElement>(null);
  const [isModalOpen, setModalOpen] = useState(false);
  const [profileToDelete, setProfileToDelete] = useState<IProfile | null>(null);

  const confirmDelete = async () => {
    if (!profileToDelete) return;

    try {
      const response = await deleteProfile(profileToDelete.token);
      if (response) {
        setProfileToDelete(null);
        setModalOpen(false);
        setIsOpen(false);
        toast.success('Профиль удалён');
      } else {
        toast.error('Ошибка удаления профиля.');
      }
    } catch {
      toast.error('Ошибка удаления профиля.');
    }
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (isOpen && panelRef.current && !panelRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleSelect = (profile: IProfile) => {
    onSelect(profile);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLLIElement>, profile: IProfile) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleSelect(profile);
    }
  };

  const filteredProfiles = profiles
    .filter(profile => profile.token !== activeProfile?.token)
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

  return (
    <div className={styles.panel} ref={panelRef}>
      {isAuthenticated && profiles.length > 1 ? (
        <Button
          variant="text"
          className={styles.title}
          aria-haspopup="true"
          aria-expanded={isOpen}
          onClick={() => setIsOpen(prev => !prev)}
        >
          <div className={styles.label}>{label}</div>
          <FiChevronDown
            className={classNames(styles.dropdown__chevronDown, {
              [styles.open]: isOpen,
            })}
          />

          {/* @ts-expect-error framer-motion types incompatible with @types/react 18 */}
          <AnimatePresence>
            {isOpen && filteredProfiles.length > 0 && (
              <motion.ul
                className={styles.menu}
                role="menu"
                initial={{ opacity: 0, y: -8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.2, ease: [0.4, 0, 0.2, 1] }}
              >
                {filteredProfiles.map((profile, i) => (
                  <motion.li
                    key={profile.token}
                    className={styles.menuItem}
                    role="menuitem"
                    tabIndex={0}
                    onClick={() => handleSelect(profile)}
                    onKeyDown={e => handleKeyDown(e, profile)}
                    initial={{ opacity: 0, x: -8 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.03 * i }}
                  >
                    <div className={styles.itemGroup}>
                      <img className={styles.itemIcon} src={StarIcon} alt="" />
                      <div className={styles.itemContent}>
                        <div className={styles.itemContent__name}>{`${profile.firstName} ${profile.lastName}`}</div>
                        <div className={styles.itemContent__bornAt}>{`[ ${formatDate(profile.bornAt)} ]`}</div>
                      </div>
                    </div>
                    <span
                      className={styles.actionIcon}
                      onClick={e => {
                        e.stopPropagation();
                        setProfileToDelete(profile);
                        setModalOpen(true);
                      }}
                      role="button"
                      tabIndex={0}
                      onKeyDown={e => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          setProfileToDelete(profile);
                          setModalOpen(true);
                        }
                      }}
                    >
                      <FiTrash2 />
                    </span>
                  </motion.li>
                ))}
              </motion.ul>
            )}
          </AnimatePresence>
        </Button>
      ) : (
        <div className={styles.title}>
          <div className={styles.label}>{label}</div>
          <Button
            className={styles.actionIcon}
            type="button"
            variant="text"
            onClick={() => {
              if (activeProfile) {
                setProfileToDelete(activeProfile);
                setModalOpen(true);
              }
            }}
            disabled={profiles.length > 1}
          >
            <FiTrash2 />
          </Button>
        </div>
      )}

      <ConfirmationModal
        isOpen={isModalOpen}
        onConfirm={confirmDelete}
        onCancel={() => setModalOpen(false)}
        title={'УДАЛИТЬ ПРОФИЛЬ'}
        message={`Вы уверены, что хотите удалить профиль? Восстановление профиля будет невозможным и все данные профиля будут удалены навсегда.`}
      />
    </div>
  );
}
